import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Loader2 } from "lucide-react";

const AUTH_IMG =
  "https://images.unsplash.com/photo-1767300258298-21f93cbe723f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2UlMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODY1MTU0OTl8MA&ixlib=rb-4.1.0&q=85";

const GoogleButton = ({ label }) => {
  const onClick = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      data-testid="google-auth-btn"
      onClick={onClick}
      className="w-full flex items-center justify-center gap-3 py-3 rounded-sm font-medium transition-colors"
      style={{ background: "#fff", color: "#1E293B" }}
    >
      <svg width="18" height="18" viewBox="0 0 48 48">
        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.6 6.1 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" />
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c2.8 0 5.4 1 7.4 2.8l5.7-5.7C33.6 6.1 29 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
        <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.7-2.6-11.3-7l-6.6 5.1C9.6 39.7 16.3 44 24 44z" />
        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.6l6.2 5.2C41.4 36.2 44 30.6 44 24c0-1.3-.1-2.3-.4-3.5z" />
      </svg>
      {label}
    </button>
  );
};

export default function Login() {
  const navigate = useNavigate();
  const { login, t } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : u.role === "trainer" ? "/trainer" : "/test");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={AUTH_IMG} alt="office" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "rgba(14,31,51,0.7)" }} />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between">
          <Logo variant="full" size="lg" onClick={() => navigate("/")} />
          <div>
            <h2 className="font-heading text-3xl leading-tight" style={{ color: "var(--text)" }}>
              Understand people.<br />Build better teams.
            </h2>
            <p className="mt-3 text-sm max-w-sm" style={{ color: "var(--text-dim)" }}>
              AMT Consulting's MBTI platform powers assessments, workshops, and corporate training.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Logo onClick={() => navigate("/")} /></div>
          <h1 className="font-heading text-3xl mb-2" style={{ color: "var(--text)" }}>
            {t("login_title")}
          </h1>
          <p className="text-sm mb-8" style={{ color: "var(--text-dim)" }}>{t("login_sub")}</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>Email</label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-amt w-full px-4 py-3 mt-1"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>{t("password")}</label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-amt w-full px-4 py-3 mt-1"
                placeholder="••••••••"
              />
            </div>
            {error && <p data-testid="login-error" className="text-sm" style={{ color: "var(--error, #EF4444)" }}>{error}</p>}
            <button data-testid="login-submit" disabled={loading} className="btn-gold w-full py-3 flex items-center justify-center gap-2">
              {loading && <Loader2 className="animate-spin" size={18} />} {t("nav_login")}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
            <span className="text-xs" style={{ color: "var(--text-dim)" }}>or</span>
            <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
          </div>
          <GoogleButton label={t("google_login")} />

          <p className="text-sm mt-8 text-center" style={{ color: "var(--text-dim)" }}>
            {t("no_account")}{" "}
            <Link to="/register" data-testid="go-register" className="gold-text font-medium">{t("nav_register")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export { GoogleButton };
